import Container from 'components/molecules/Container/Container';
import { HelmetAndPageAnnouncer } from 'components/organisms/HelmetAndPageAnnouncer/HelmetAndPageAnnouncer';
import {
  SupportedLocale,
  useStartPersonalityTestAssessmentMutation,
} from 'generated/graphql';
import {
  BEHAVIOURAL_TEST_LONG_LABEL,
  shouldUseBehaviouralLabel,
} from 'helpers/behaviouralRelabel';
import { useDisableCopyAndRightClick } from 'hooks/useDisableCopyAndRightClick';
import { FC, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import {
  Loader,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import DesktopPersonalityTest from 'views/Test/Tests/PersonalityTest/DesktopPersonalityTest/DesktopPersonalityTest';
import MobilePersonalityTest from 'views/Test/Tests/PersonalityTest/MobilePersonalityTest/MobilePersonalityTest';

interface IPersonalityTestRunner {
  actionCandidateId: string;
  locale?: SupportedLocale;
  /** Where the already-completed notification exits to. */
  exitUrl: string;
  onFinishTest: () => void;
  // TEMP HACK — "Personality" → "Behavioural" relabel (per-client, temporary).
  // See helpers/behaviouralRelabel.ts
  usesBehaviouralLabel?: boolean;
}

const PersonalityTestRunner: FC<IPersonalityTestRunner> = ({
  actionCandidateId,
  locale,
  exitUrl,
  onFinishTest,
  usesBehaviouralLabel,
}) => {
  const { handleMsgType } = useNotification();
  const { t } = useTranslation();
  useDisableCopyAndRightClick();

  const [startAssessmentMutation, startAssessmentResponse] =
    useStartPersonalityTestAssessmentMutation({
      onError: error => {
        handleMsgType({
          type: TNotification.error,
          message: error?.message,
        });
      },
      variables: {
        id: actionCandidateId,
        locale,
      },
    });

  useEffect(() => {
    startAssessmentMutation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const assessmentId =
    startAssessmentResponse.data?.startPersonalityTestAssessment.assessmentId;

  const getContent = () => {
    if (startAssessmentResponse.loading) {
      return (
        <Container className="page-loader-wrapper">
          <Loader variant="bubbles" />
        </Container>
      );
    }
    if (assessmentId) {
      return isMobile ? (
        <MobilePersonalityTest
          assessmentId={assessmentId}
          onFinishTest={onFinishTest}
        />
      ) : (
        <DesktopPersonalityTest
          assessmentId={assessmentId}
          exitUrl={exitUrl}
          onFinishTest={onFinishTest}
        />
      );
    }
    return null;
  };

  return (
    <>
      <HelmetAndPageAnnouncer
        // TEMP HACK — "Personality" → "Behavioural" relabel (per-client,
        // temporary). See helpers/behaviouralRelabel.ts
        pageTitle={
          shouldUseBehaviouralLabel(usesBehaviouralLabel)
            ? BEHAVIOURAL_TEST_LONG_LABEL
            : t('test.instructions.personality.name')
        }
      />

      {getContent()}
    </>
  );
};

export default PersonalityTestRunner;
